import { TRootState } from "../../store/create-store";
import { useDispatch, useSelector } from "react-redux";
import SectionTitle from "../../components/SectionTitle";

import styles from "./Categories.module.css";
import { useCallback, useEffect, useState } from "react";
import update from "immutability-helper";
import DraggableCategory from "./DraggableCategory";
import Switch from "../../components/Switch";
import ColumnButton from "../../components/ColumnButton";
import { rootActions } from "../../reducers/root.slice";
import Category from "./Category";
import withErrorBoundary from "../../hocs/withErrorBoundary";
import Typography from "../../components/Typography";
import {
  selectLeftPanelState,
  selectRightPanelState,
  selectRootState,
} from "../../selectors";

const Categories = () => {
  const dispatch = useDispatch();
  const {
    graphData,
    isCategorical,
    isConvertedCategorical,
    originalCategories,
  } = useSelector(selectCategories);
  const showCategories = isCategorical || isConvertedCategorical;

  const [categories, setCategories] = useState<string[]>([]);

  const getCategories = useCallback(() => {
    if (isConvertedCategorical) {
      if (!originalCategories) {
        throw new Error("originalCategories is undefined");
      }

      return [...originalCategories];
    }

    if (!graphData || graphData.length === 1) {
      return [];
    } else {
      return graphData
        .filter((datum) => datum.type !== "cone" && datum.mode !== "lines")
        .map((datum) => datum?.name || "Unknown");
    }
  }, [graphData]);

  useEffect(() => {
    setCategories(getCategories());
  }, [graphData]);

  const moveCategory = useCallback((dragIndex: number, hoverIndex: number) => {
    setCategories((prevCards) =>
      update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex]],
        ],
      })
    );
  }, []);

  const renderDraggableCategory = useCallback(
    (datum: string, index: number) => {
      return (
        <DraggableCategory
          key={datum}
          id={datum}
          index={index}
          moveCategory={moveCategory}
        >
          {datum}
        </DraggableCategory>
      );
    },
    []
  );

  const renderCategory = (datum: string) => (
    <Category key={datum}>{datum}</Category>
  );

  const applyDisabled = useCallback(
    () => getCategories().every((category, i) => category === categories[i]),
    [categories]
  );

  if (!showCategories) {
    return null;
  }

  return (
    <>
      <SectionTitle>Categories</SectionTitle>

      {showCategories && categories.length > 0 && (
        <>
          <label className={styles.field}>
            <Typography variant="body">Show as continuous </Typography>
            <Switch
              value={!!isConvertedCategorical}
              onChange={() => {
                if (!isConvertedCategorical) {
                  dispatch(rootActions.convertToContinuous(categories));
                } else {
                  dispatch(rootActions.revertToCategorical());
                }
              }}
            />
          </label>

          <ul className={styles.list}>
            {categories.map(
              isConvertedCategorical ? renderDraggableCategory : renderCategory
            )}
          </ul>

          {isConvertedCategorical && (
            <div className={styles.buttonRow}>
              <ColumnButton
                onClick={() => {
                  dispatch(rootActions.applyReorder(categories));
                }}
                isActive={false}
                disabled={applyDisabled()}
              >
                Apply
              </ColumnButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

const selectCategories = (state: TRootState) => {
  const { selectedColumn } = selectLeftPanelState(state);
  const { categoricalColumnsAsContinuous } = selectRightPanelState(state);
  const { graphData, categoricalColumns } = selectRootState(state);

  return {
    graphData,
    isCategorical: selectedColumn
      ? categoricalColumns.includes(selectedColumn)
      : undefined,
    isConvertedCategorical: selectedColumn
      ? Object.keys(categoricalColumnsAsContinuous).includes(selectedColumn)
      : undefined,
    originalCategories: selectedColumn
      ? categoricalColumnsAsContinuous[selectedColumn]
      : undefined,
  };
};

export default withErrorBoundary(Categories);
